import * as Types from '@frameio/graphql-client/generated/types';

import { TypedDocumentNode as DocumentNode } from '@graphql-typed-document-node/core';
import gql from 'graphql-tag';
import { OriginalMediaInfo } from './OriginalMediaInfo';
import { DocumentPage } from './DocumentPage';

import { useQuery, useLazyQuery, useMutation, useFragment, useSubscription } from '@apollo/client';
import type { QueryHookOptions, LazyQueryHookOptions, MutationHookOptions, SubscriptionHookOptions, UseFragmentOptions } from '@apollo/client';

export type CompleteAudioMediaFragment = { __typename?: 'AudioMedia', duration?: number | null, filesize?: number | null, fps?: number | null, hlsManifest?: string | null, id: string, insertedAt?: string | null, mimeType?: string | null, transcodedAt?: string | null, audioTranscodes?: Array<{ __typename?: 'AudioTranscodeInfo', downloadUrl?: string | null, encodeStatus: Types.EncodeStatusEnum, key: string, streamUrl?: string | null }> | null, original?: { __typename?: 'OriginalInfo', key: string, downloadUrl?: string | null, filesizeInBytes?: number | null, inlineUrl?: string | null, codec?: string | null } | null };

export type CompleteDocumentMediaFragment = { __typename?: 'DocumentMedia', filesize?: number | null, id: string, insertedAt?: string | null, mimeType?: string | null, pageCount?: number | null, pdfProxyTranscode?: { __typename?: 'PdfProxyTranscodeInfo', url?: string | null, encodeStatus: Types.EncodeStatusEnum } | null, pdfDownload?: { __typename?: 'PdfDownloadInfo', url?: string | null } | null, metadata?: { __typename?: 'DocumentMetadata', id?: string | null, originalHeight?: number | null, originalWidth?: number | null, baseUrl?: string | null } | null, original?: { __typename?: 'OriginalInfo', key: string, downloadUrl?: string | null, filesizeInBytes?: number | null, inlineUrl?: string | null, codec?: string | null } | null, pages: Array<{ __typename?: 'Page', height?: number | null, width?: number | null }> };

export type CompleteDocumentMediaWithoutPdfTranscodeFragment = { __typename?: 'DocumentMedia', filesize?: number | null, id: string, insertedAt?: string | null, mimeType?: string | null, pageCount?: number | null, metadata?: { __typename?: 'DocumentMetadata', id?: string | null, originalHeight?: number | null, originalWidth?: number | null, baseUrl?: string | null } | null, original?: { __typename?: 'OriginalInfo', key: string, downloadUrl?: string | null, filesizeInBytes?: number | null, inlineUrl?: string | null, codec?: string | null } | null, pages: Array<{ __typename?: 'Page', height?: number | null, width?: number | null }> };

export type CompleteImageMediaFragment = { __typename?: 'ImageMedia', filesize?: number | null, id: string, insertedAt?: string | null, mimeType?: string | null, imageTranscodes?: Array<{ __typename?: 'ImageTranscodeInfo', downloadUrl?: string | null, encodeStatus: Types.EncodeStatusEnum, key: string, streamUrl?: string | null }> | null, metadata?: { __typename?: 'ImageMetadata', id?: string | null, originalHeight?: number | null, originalWidth?: number | null, hasAlpha?: boolean | null } | null, original?: { __typename?: 'OriginalInfo', key: string, downloadUrl?: string | null, filesizeInBytes?: number | null, inlineUrl?: string | null, codec?: string | null } | null };

export type CompleteInteractiveMediaFragment = { __typename?: 'InteractiveMedia', filesize?: number | null, id: string, insertedAt?: string | null, mimeType?: string | null, original?: { __typename?: 'OriginalInfo', key: string, downloadUrl?: string | null, filesizeInBytes?: number | null, inlineUrl?: string | null, codec?: string | null } | null };

export type CompleteInteractiveLiveMediaFragment = { __typename?: 'InteractiveLiveMedia', id: string, insertedAt?: string | null, mimeType?: string | null, url?: string | null };

export type CompleteUnsupportedMediaFragment = { __typename?: 'UnsupportedMedia', filesize?: number | null, id: string, insertedAt?: string | null, mimeType?: string | null, original?: { __typename?: 'OriginalInfo', key: string, downloadUrl?: string | null, filesizeInBytes?: number | null, inlineUrl?: string | null, codec?: string | null } | null };

export type CompleteVideoMediaFragment = { __typename?: 'VideoMedia', duration?: number | null, filesize?: number | null, fps?: number | null, frames?: number | null, hlsManifest?: string | null, id: string, insertedAt?: string | null, mimeType?: string | null, timecode?: string | null, transcodedAt?: string | null, drm?: { __typename?: 'Drm', fairplayStreamingCertificateUrl?: string | null, fairplayStreamingLicenseUrl?: string | null, widevineLicenseUrl?: string | null } | null, imageTranscodes?: Array<{ __typename?: 'ImageTranscodeInfo', downloadUrl?: string | null, encodeStatus: Types.EncodeStatusEnum, key: string, streamUrl?: string | null }> | null, metadata?: { __typename?: 'VideoMetadata', id?: string | null, originalHeight?: number | null, originalWidth?: number | null, isVfr?: boolean | null } | null, original?: { __typename?: 'OriginalInfo', key: string, downloadUrl?: string | null, filesizeInBytes?: number | null, inlineUrl?: string | null, codec?: string | null } | null, videoTranscodes?: Array<{ __typename?: 'VideoTranscodeInfo', codec?: string | null, downloadUrl?: string | null, encodeStatus: Types.EncodeStatusEnum, filesizeInBytes?: number | null, height?: number | null, key: string, streamUrl?: string | null, required: boolean, width?: number | null }> | null };

export type CompleteModelMediaFragment = { __typename?: 'ModelMedia', duration?: number | null, filesize?: number | null, fps?: number | null, frames?: number | null, hlsManifest?: string | null, id: string, insertedAt?: string | null, mimeType?: string | null, timecode?: string | null, transcodedAt?: string | null, drm?: { __typename?: 'Drm', fairplayStreamingCertificateUrl?: string | null, fairplayStreamingLicenseUrl?: string | null, widevineLicenseUrl?: string | null } | null, imageTranscodes?: Array<{ __typename?: 'ImageTranscodeInfo', downloadUrl?: string | null, encodeStatus: Types.EncodeStatusEnum, key: string, streamUrl?: string | null }> | null, metadata?: { __typename?: 'ModelMetadata', id?: string | null, originalHeight?: number | null, originalWidth?: number | null } | null, original?: { __typename?: 'OriginalInfo', key: string, downloadUrl?: string | null, filesizeInBytes?: number | null, inlineUrl?: string | null, codec?: string | null } | null, videoTranscodes?: Array<{ __typename?: 'VideoTranscodeInfo', codec?: string | null, downloadUrl?: string | null, encodeStatus: Types.EncodeStatusEnum, filesizeInBytes?: number | null, height?: number | null, key: string, streamUrl?: string | null, required: boolean, width?: number | null }> | null, modelTranscodes?: Array<{ __typename?: 'ModelTranscodeInfo', key: string, codec?: string | null, encodeStatus: Types.EncodeStatusEnum, downloadUrl?: string | null, streamUrl?: string | null, filesizeInBytes?: number | null }> | null };

export const CompleteAudioMedia = /*#__PURE__*/ gql`
    fragment CompleteAudioMedia on AudioMedia {
  audioTranscodes {
    downloadUrl
    encodeStatus
    key
    streamUrl
  }
  duration
  filesize
  fps
  hlsManifest
  id
  insertedAt
  mimeType
  original {
    ...OriginalMediaInfo
  }
  transcodedAt
}
    ${OriginalMediaInfo}` as unknown as DocumentNode<CompleteAudioMediaFragment, unknown>;
export const CompleteDocumentMediaWithoutPdfTranscode = /*#__PURE__*/ gql`
    fragment CompleteDocumentMediaWithoutPdfTranscode on DocumentMedia {
  filesize
  id
  insertedAt
  metadata {
    id
    originalHeight
    originalWidth
    baseUrl
  }
  mimeType
  original {
    ...OriginalMediaInfo
  }
  pageCount
  pages {
    ...DocumentPage
  }
}
    ${OriginalMediaInfo}
${DocumentPage}` as unknown as DocumentNode<CompleteDocumentMediaWithoutPdfTranscodeFragment, unknown>;
export const CompleteDocumentMedia = /*#__PURE__*/ gql`
    fragment CompleteDocumentMedia on DocumentMedia {
  ...CompleteDocumentMediaWithoutPdfTranscode
  pdfProxyTranscode {
    url
    encodeStatus
  }
  pdfDownload {
    url
  }
}
    ${CompleteDocumentMediaWithoutPdfTranscode}` as unknown as DocumentNode<CompleteDocumentMediaFragment, unknown>;
export const CompleteImageMedia = /*#__PURE__*/ gql`
    fragment CompleteImageMedia on ImageMedia {
  filesize
  id
  imageTranscodes {
    downloadUrl
    encodeStatus
    key
    streamUrl
  }
  insertedAt
  metadata {
    id
    originalHeight
    originalWidth
    hasAlpha
  }
  mimeType
  original {
    ...OriginalMediaInfo
  }
}
    ${OriginalMediaInfo}` as unknown as DocumentNode<CompleteImageMediaFragment, unknown>;
export const CompleteInteractiveMedia = /*#__PURE__*/ gql`
    fragment CompleteInteractiveMedia on InteractiveMedia {
  filesize
  id
  insertedAt
  mimeType
  original {
    ...OriginalMediaInfo
  }
}
    ${OriginalMediaInfo}` as unknown as DocumentNode<CompleteInteractiveMediaFragment, unknown>;
export const CompleteInteractiveLiveMedia = /*#__PURE__*/ gql`
    fragment CompleteInteractiveLiveMedia on InteractiveLiveMedia {
  id
  insertedAt
  mimeType
  url
}
    ` as unknown as DocumentNode<CompleteInteractiveLiveMediaFragment, unknown>;
export const CompleteUnsupportedMedia = /*#__PURE__*/ gql`
    fragment CompleteUnsupportedMedia on UnsupportedMedia {
  filesize
  id
  insertedAt
  mimeType
  original {
    ...OriginalMediaInfo
  }
}
    ${OriginalMediaInfo}` as unknown as DocumentNode<CompleteUnsupportedMediaFragment, unknown>;
export const CompleteVideoMedia = /*#__PURE__*/ gql`
    fragment CompleteVideoMedia on VideoMedia {
  drm {
    fairplayStreamingCertificateUrl
    fairplayStreamingLicenseUrl
    widevineLicenseUrl
  }
  duration
  filesize
  fps
  frames
  hlsManifest
  id
  imageTranscodes {
    downloadUrl
    encodeStatus
    key
    streamUrl
  }
  insertedAt
  metadata {
    id
    originalHeight
    originalWidth
    isVfr
  }
  mimeType
  original {
    ...OriginalMediaInfo
  }
  timecode
  transcodedAt
  videoTranscodes {
    codec
    downloadUrl
    encodeStatus
    filesizeInBytes
    height
    key
    streamUrl
    required
    width
  }
}
    ${OriginalMediaInfo}` as unknown as DocumentNode<CompleteVideoMediaFragment, unknown>;
export const CompleteModelMedia = /*#__PURE__*/ gql`
    fragment CompleteModelMedia on ModelMedia {
  drm {
    fairplayStreamingCertificateUrl
    fairplayStreamingLicenseUrl
    widevineLicenseUrl
  }
  duration
  filesize
  fps
  frames
  hlsManifest
  id
  imageTranscodes {
    downloadUrl
    encodeStatus
    key
    streamUrl
  }
  insertedAt
  metadata {
    id
    originalHeight
    originalWidth
  }
  mimeType
  original {
    ...OriginalMediaInfo
  }
  timecode
  transcodedAt
  videoTranscodes {
    codec
    downloadUrl
    encodeStatus
    filesizeInBytes
    height
    key
    streamUrl
    required
    width
  }
  modelTranscodes {
    key
    codec
    encodeStatus
    downloadUrl
    streamUrl
    filesizeInBytes
  }
}
    ${OriginalMediaInfo}` as unknown as DocumentNode<CompleteModelMediaFragment, unknown>;

        

        
export const useCompleteAudioMediaFragment = (
  input: string | Omit<UseFragmentOptions<CompleteAudioMediaFragment, any>, 'fragment' | 'fragmentName'> | undefined | null
) => {
  const options = (() => {
    if (!input) return { from: null };
    if (typeof input === 'string') return { from: { id: input, __typename: 'AudioMedia' } };
    return input;
  })();

  return useFragment<CompleteAudioMediaFragment>({
    fragment: CompleteAudioMedia,
    fragmentName: 'CompleteAudioMedia',
    ...options,
  });
};
  
        
        

        
export const useCompleteDocumentMediaFragment = (
  input: string | Omit<UseFragmentOptions<CompleteDocumentMediaFragment, any>, 'fragment' | 'fragmentName'> | undefined | null
) => {
  const options = (() => {
    if (!input) return { from: null };
    if (typeof input === 'string') return { from: { id: input, __typename: 'DocumentMedia' } };
    return input;
  })();

  return useFragment<CompleteDocumentMediaFragment>({
    fragment: CompleteDocumentMedia,
    fragmentName: 'CompleteDocumentMedia',
    ...options,
  });
};
  
        
        

        
export const useCompleteDocumentMediaWithoutPdfTranscodeFragment = (
  input: string | Omit<UseFragmentOptions<CompleteDocumentMediaWithoutPdfTranscodeFragment, any>, 'fragment' | 'fragmentName'> | undefined | null
) => {
  const options = (() => {
    if (!input) return { from: null };
    if (typeof input === 'string') return { from: { id: input, __typename: 'DocumentMedia' } };
    return input;
  })();

  return useFragment<CompleteDocumentMediaWithoutPdfTranscodeFragment>({
    fragment: CompleteDocumentMediaWithoutPdfTranscode,
    fragmentName: 'CompleteDocumentMediaWithoutPdfTranscode',
    ...options,
  });
};
  
        
        

        
export const useCompleteImageMediaFragment = (
  input: string | Omit<UseFragmentOptions<CompleteImageMediaFragment, any>, 'fragment' | 'fragmentName'> | undefined | null
) => {
  const options = (() => {
    if (!input) return { from: null };
    if (typeof input === 'string') return { from: { id: input, __typename: 'ImageMedia' } };
    return input;
  })();

  return useFragment<CompleteImageMediaFragment>({
    fragment: CompleteImageMedia,
    fragmentName: 'CompleteImageMedia',
    ...options,
  });
};
  
        
        

        
export const useCompleteInteractiveMediaFragment = (
  input: string | Omit<UseFragmentOptions<CompleteInteractiveMediaFragment, any>, 'fragment' | 'fragmentName'> | undefined | null
) => {
  const options = (() => {
    if (!input) return { from: null };
    if (typeof input === 'string') return { from: { id: input, __typename: 'InteractiveMedia' } };
    return input;
  })();

  return useFragment<CompleteInteractiveMediaFragment>({
    fragment: CompleteInteractiveMedia,
    fragmentName: 'CompleteInteractiveMedia',
    ...options,
  });
};
  
        
        

        
export const useCompleteInteractiveLiveMediaFragment = (
  input: string | Omit<UseFragmentOptions<CompleteInteractiveLiveMediaFragment, any>, 'fragment' | 'fragmentName'> | undefined | null
) => {
  const options = (() => {
    if (!input) return { from: null };
    if (typeof input === 'string') return { from: { id: input, __typename: 'InteractiveLiveMedia' } };
    return input;
  })();

  return useFragment<CompleteInteractiveLiveMediaFragment>({
    fragment: CompleteInteractiveLiveMedia,
    fragmentName: 'CompleteInteractiveLiveMedia',
    ...options,
  });
};
  
        
        

        
export const useCompleteUnsupportedMediaFragment = (
  input: string | Omit<UseFragmentOptions<CompleteUnsupportedMediaFragment, any>, 'fragment' | 'fragmentName'> | undefined | null
) => {
  const options = (() => {
    if (!input) return { from: null };
    if (typeof input === 'string') return { from: { id: input, __typename: 'UnsupportedMedia' } };
    return input;
  })();

  return useFragment<CompleteUnsupportedMediaFragment>({
    fragment: CompleteUnsupportedMedia,
    fragmentName: 'CompleteUnsupportedMedia',
    ...options,
  });
};
  
        
        

        
export const useCompleteVideoMediaFragment = (
  input: string | Omit<UseFragmentOptions<CompleteVideoMediaFragment, any>, 'fragment' | 'fragmentName'> | undefined | null
) => {
  const options = (() => {
    if (!input) return { from: null };
    if (typeof input === 'string') return { from: { id: input, __typename: 'VideoMedia' } };
    return input;
  })();

  return useFragment<CompleteVideoMediaFragment>({
    fragment: CompleteVideoMedia,
    fragmentName: 'CompleteVideoMedia',
    ...options,
  });
};
  
        
        

        
export const useCompleteModelMediaFragment = (
  input: string | Omit<UseFragmentOptions<CompleteModelMediaFragment, any>, 'fragment' | 'fragmentName'> | undefined | null
) => {
  const options = (() => {
    if (!input) return { from: null };
    if (typeof input === 'string') return { from: { id: input, __typename: 'ModelMedia' } };
    return input;
  })();

  return useFragment<CompleteModelMediaFragment>({
    fragment: CompleteModelMedia,
    fragmentName: 'CompleteModelMedia',
    ...options,
  });
};
  
        